'use client';

import { useProductsBrief } from '@/domain/products/hooks';
import { type ProductBrief } from '@/domain/products/types';
import { useRoadmapsVersions } from '@/domain/roadmap/hooks';
import { type PlatformVersionDTO, type RoadMapUpdate } from '@/domain/roadmap/types';
import { useAuth } from '@/hooks/useAuth';
import { Autocomplete } from '@andritzot/metris-web-ui/inputs/autocomplete';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { useCallback } from 'react';
import { type Control, Controller, type UseFormSetValue } from 'react-hook-form';

import { FieldActionsWrapper } from './shared/FieldActionsWrapper';

interface ProductFieldProps {
  viewOnly: boolean;
  control: Control<RoadMapUpdate, unknown>;
  setValue: UseFormSetValue<RoadMapUpdate>;
  setRoadmapVersions: (versions: PlatformVersionDTO[]) => void;
}

export function ProductField({ viewOnly, control, setValue, setRoadmapVersions }: Readonly<ProductFieldProps>) {
  const { data: products } = useProductsBrief();
  const { user } = useAuth();
  const { data: versions } = useRoadmapsVersions(!!user);

  const handleChangeProduct = useCallback(
    (product: ProductBrief | null) => {
      setValue('affectedVersions', []);
      setValue('fixVersions', []);
      const filteredVersions =
        product == null ? [] : versions?.filter((v) => v.release?.idPlatform === product.idPlatform) ?? [];
      setRoadmapVersions(filteredVersions);
    },
    [setRoadmapVersions, setValue, versions],
  );

  return (
    <Box sx={{ py: 0.75, width: '100%' }}>
      <Controller
        name="product"
        control={control}
        rules={{ required: true }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <FieldActionsWrapper copyValue={value?.product ?? ''}>
            <Autocomplete<ProductBrief, false, false, false>
              value={value ?? null}
              onChange={(_, newValue) => {
                handleChangeProduct(newValue);
                onChange(newValue);
              }}
              options={products ?? []}
              getOptionLabel={(option) => option.product}
              isOptionEqualToValue={(option, v) => option.idProduct === v.idProduct}
              label="Product *"
              placeholder="Search product..."
              disabled={viewOnly}
              fullWidth
              size="small"
              sx={error ? { '& .MuiOutlinedInput-root': { borderColor: 'error.main' } } : undefined}
            />
          </FieldActionsWrapper>
        )}
      />
    </Box>
  );
}
